import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MODEL_TABS } from "@shared/copy";
import { api } from "../lib/api";
import type { ModelRecordView } from "@shared/types";
import { LineageGraph } from "../components/LineageGraph";
import { AssumptionCard } from "../components/AssumptionCard";
import { Assistant } from "../components/Assistant";
import { MapView } from "../components/MapView";
import { StatusPill } from "../components/Status";

export function ModelRecord() {
  const { slug } = useParams();
  const [view, setView] = useState<ModelRecordView | null>(null);
  const [tab, setTab] = useState<(typeof MODEL_TABS)[number]>("Overview");

  useEffect(() => {
    if (!slug) return;
    api<ModelRecordView>(`/api/models/${slug}`).then(setView).catch(() => setView(null));
  }, [slug]);

  if (!view) return <section className="page-hero"><div className="wrap"><p className="muted">Loading model record…</p></div></section>;

  return (
    <>
      <MapView lat={-19.3} lng={146.78} layers={view.layers} isDemo={view.synthetic || view.assumptions.some((a) => a.synthetic)} zoom={10} />
      <div className="wrap record-head">
        <p className="kicker">Model Record · {view.onenetworkId}</p>
        <h1>{view.name}</h1>
        <div className="record-meta">
          <Link to={`/system/${view.systemSlug}`}>{view.systemName}</Link>
          <span>{view.software}</span>
          <span>{view.dimensionality}</span>
          <span>{view.owner}</span>
          <StatusPill status={view.epistemic} />
        </div>
        <p className="summary">{view.summary}</p>
        <div className="dims">
          {view.dimensions.map((d) => (
            <div className="dim" key={d.key}>
              <div className="label">{d.label}</div>
              <div className="state">{d.state}</div>
              <div className="explain">{d.evidence}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="wrap">
        <div className="tabs">
          {MODEL_TABS.map((t) => (
            <button key={t} className={tab === t ? "active" : ""} type="button" onClick={() => setTab(t)}>
              {t}
            </button>
          ))}
        </div>
        <div className="panel">
          {tab === "Overview" && (
            <div className="grid-2">
              <article className="card"><h3>Study</h3><p>{view.studyTitle ?? "Not documented"}</p></article>
              <article className="card"><h3>Adoption</h3><p>{view.adoptionStatus ?? "Not documented"}</p></article>
              <article className="card"><h3>Files</h3><p>{view.filesAvailable ? "Available" : "Not available on Floodplains.net"}</p></article>
              <article className="card"><h3>Limitations</h3><p>{view.knownLimitations ?? "Not documented"}</p></article>
            </div>
          )}
          {tab === "Lineage" && <LineageGraph nodes={view.lineage} />}
          {tab === "Geometry & terrain" && (
            <article className="card">
              <p>{view.geometryBasis ?? "Not documented"}</p>
              <p className="muted">{view.terrainSource} {view.terrainDate}</p>
            </article>
          )}
          {tab === "Hydrology" && <p>{view.hydrologyInput ?? "Not documented in the stored excerpt."}</p>}
          {tab === "Hydraulics" && (
            <p>
              {view.software} {view.softwareVersion ?? ""} · {view.dimensionality}. Grid/geometry: {view.geometryBasis ?? "Not documented"}.
            </p>
          )}
          {tab === "Calibration" && <p>{view.calibrationEvents ?? "Not documented"}</p>}
          {tab === "Validation" && (
            <p>
              {view.validationEvents ?? "Not documented in the stored excerpt. A good calibration statistic, if later attached, would not by itself mean low uncertainty."}
            </p>
          )}
          {tab === "Assumptions" && (
            <div className="grid-2">
              {view.assumptions.map((a) => (
                <AssumptionCard key={a.id} a={a} />
              ))}
            </div>
          )}
          {tab === "Sensitivity" && <p>{view.sensitivityTesting ?? "Not extracted. DEMO sensitivity, where present, is labelled."}</p>}
          {tab === "Decisions" &&
            view.decisions.map((d) => (
              <article className="ev-item" key={d.id}>
                <StatusPill status={d.epistemic} />
                <h3>{d.title}</h3>
                <p>{d.summary}</p>
              </article>
            ))}
          {tab === "Evidence" &&
            view.evidence.map((e) => (
              <article className="ev-item" key={e.id}>
                <h3>{e.title}</h3>
                <p className="muted small">{e.sourceOrganisation} · {e.publishedAt}</p>
                {e.sourceUrl && <a href={e.sourceUrl} rel="noreferrer">Canonical source</a>}
              </article>
            ))}
          {tab === "Contributors" && (
            <p className="muted">Commissioning body and consultancy are listed where public. There is no ranking.</p>
          )}
        </div>
      </div>
      <Assistant system={view.systemSlug} />
    </>
  );
}
